import React, { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api, { examInfoAPI } from '../lib/api';
import { 
  BookOpen, Target, Clock, Award, TrendingUp, X,
  FileText, CheckCircle, Info, Calendar, Bell, Users, Briefcase,
  GraduationCap, Building2, DollarSign, MapPin, Star, ChevronRight,
  ArrowLeft, ExternalLink, Lightbulb, BarChart3, Shield, Rocket, Trophy,
  RefreshCw, Zap, Globe, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { getExamSubjects, getSubjectChapters } from '../config/examSubjects';
import LoadingSpinner from '../components/LoadingSpinner';

// Comprehensive exam information with career opportunities and eligibility
export const EXAM_CAREER_INFO = {
  "jee_main": {
    fullName: "Joint Entrance Examination (Main)",
    conductedBy: "National Testing Agency (NTA)",
    overview: "JEE Main is one of India's most competitive engineering entrance exams, conducted by NTA twice a year in CBT mode. It's the primary gateway to 31 NITs, 26 IIITs, and 2175+ other centrally funded and participating technical institutions. Top 2.5 lakh qualifiers also become eligible for JEE Advanced (IIT admission). In 2025, over 13 lakh candidates appeared for Session 1 alone.",
    examFrequency: "Twice a year (January & April sessions)",
    totalCandidates: "13+ Lakh annually",
    officialWebsite: "https://jeemain.nta.nic.in",
    eligibility: {
      age: "No age limit (removed from 2024 onwards)",
      qualification: "Class 12th (or equivalent) with Physics, Chemistry & Mathematics",
      minimumMarks: "75% in Class 12th (65% for SC/ST) OR Top 20 percentile in respective board",
      attempts: "Maximum 3 consecutive years from year of passing Class 12",
      yearOfPassing: "2025 or 2026 or appearing in 2027",
      nationality: "Indian Nationals, OCI/PIO holders, and foreign nationals"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      papers: "Paper 1 (B.E./B.Tech), Paper 2A (B.Arch), Paper 2B (B.Planning)",
      totalQuestions: 90,
      questionsToAttempt: 75,
      duration: "180 minutes (3 hours)",
      totalMarks: 300,
      marking: "+4 for correct MCQ, -1 for incorrect MCQ, +4 for correct numerical (no negative)",
      sections: [
        { name: "Physics", questions: 30, toAttempt: 25, marks: 100, detail: "20 MCQ (Section A) + 10 Numerical, attempt any 5 (Section B)" },
        { name: "Chemistry", questions: 30, toAttempt: 25, marks: 100, detail: "20 MCQ (Section A) + 10 Numerical, attempt any 5 (Section B)" },
        { name: "Mathematics", questions: 30, toAttempt: 25, marks: 100, detail: "20 MCQ (Section A) + 10 Numerical, attempt any 5 (Section B)" }
      ],
      languages: ["English", "Hindi", "Assamese", "Bengali", "Gujarati", "Kannada", "Malayalam", "Marathi", "Odia", "Punjabi", "Tamil", "Telugu", "Urdu"]
    },
    applicationFee: {
      general: "₹1000 (Male UR/EWS/OBC-NCL)",
      female: "₹900 (All Categories)",
      scSt: "₹500 (SC/ST/PwD/Transgender)"
    },
    jobOpportunities: [
      { title: "Software Engineer", salary: "₹6-60 LPA", companies: "Google, Microsoft, Amazon, TCS, Infosys", description: "Develop software applications, mobile apps, and enterprise solutions" },
      { title: "Data Scientist / AI Engineer", salary: "₹8-40 LPA", companies: "Meta, Netflix, Flipkart, Swiggy, OpenAI", description: "Analyze data and build ML/AI models to drive business decisions" },
      { title: "Product Manager", salary: "₹15-50 LPA", companies: "Google, Amazon, Flipkart, Paytm, Razorpay", description: "Lead product development from conception to launch" },
      { title: "Design Engineer", salary: "₹5-25 LPA", companies: "L&T, Tata Motors, Mahindra, ISRO, Boeing India", description: "Design and build mechanical, electrical, and civil systems" },
      { title: "Research Scientist", salary: "₹8-30 LPA", companies: "ISRO, DRDO, BARC, IITs, Microsoft Research", description: "Conduct research in science & technology" },
      { title: "Quantitative Analyst", salary: "₹20-75 LPA", companies: "Goldman Sachs, Morgan Stanley, Tower Research", description: "Apply mathematical models to financial markets" }
    ],
    collegePackages: [
      { college: "IITs (via JEE Advanced)", avgPackage: "₹20-28 LPA", highestPackage: "₹2.4+ Cr" },
      { college: "NITs (Top 5)", avgPackage: "₹12-20 LPA", highestPackage: "₹1+ Cr" },
      { college: "BITS Pilani", avgPackage: "₹18-24 LPA", highestPackage: "₹1.5+ Cr" },
      { college: "IIITs (Top 5)", avgPackage: "₹12-18 LPA", highestPackage: "₹80+ LPA" },
      { college: "DTU / NSUT Delhi", avgPackage: "₹12-16 LPA", highestPackage: "₹1+ Cr" }
    ],
    topColleges: ["NIT Trichy", "NIT Warangal", "NIT Surathkal", "IIIT Hyderabad", "NIT Calicut", "NIT Rourkela", "BITS Pilani", "DTU Delhi", "NSUT Delhi", "VIT Vellore"],
    whyPrepare: [
      "Access to 31 NITs with 23,000+ B.Tech seats",
      "26 IIITs with excellent placement records",
      "Score accepted by 2175+ participating institutions across India",
      "Gateway to JEE Advanced for IIT admission (top 2.5 lakh)",
      "Opens doors to highest-paying tech jobs in India & abroad",
      "Two attempts per year maximize your chances"
    ],
    preparationTips: [
      "Master NCERT concepts thoroughly for Class 11 & 12 - foundation is key",
      "Solve 10+ years of Previous Year Questions under timed conditions",
      "Focus on Physics numerical, Chemistry reactions & Mathematics problem-solving",
      "Practice 50+ questions daily across all subjects",
      
    ],
    expectedCutoff2026: {
      general: "93-96 percentile (for JEE Advanced eligibility)",
      obc: "78-82 percentile",
      sc: "52-58 percentile",
      st: "42-48 percentile"
    },
    importantDates2026: {
      session1Registration: "28 October - 22 November 2025",
      session1Exam: "22 - 31 January 2026",
      session1AnswerKey: "First week of February 2026",
      session1Result: "16 February 2026 (declared)",
      session2Registration: "1 - 25 February 2026",
      session2Exam: "2 - 9 April 2026",
      session2Result: "April 2026",
      counselling: "JoSAA counselling June-July 2026"
    }
  },
  "neet_ug": {
    fullName: "National Eligibility cum Entrance Test (UG)",
    conductedBy: "National Testing Agency (NTA)",
    overview: "NEET UG is India's single largest entrance exam for admission to MBBS, BDS, AYUSH, Veterinary, and other medical/allied health science courses across the country. With over 24 lakh candidates appearing annually and approximately 1,10,000+ MBBS seats available in 2319+ medical institutions, it is one of the most competitive exams in the world. NEET score is mandatory for all medical admissions in India including AIIMS and JIPMER.",
    examFrequency: "Once a year (May)",
    totalCandidates: "24+ Lakh annually",
    officialWebsite: "https://neet.nta.nic.in",
    eligibility: {
      age: "17-25 years as of 31st December of admission year (30 years for SC/ST/OBC/PwD)",
      qualification: "Class 12th (or equivalent) with Physics, Chemistry, Biology/Biotechnology and English",
      minimumMarks: "50% aggregate in PCB (40% for SC/ST/OBC, 45% for PwD)",
      attempts: "No limit on attempts (within age limit)",
      yearOfPassing: "2025 or 2026 or appearing in 2027",
      nationality: "Indian Nationals, NRIs, OCIs, PIOs, and Foreign Nationals"
    },
    examPattern: {
      mode: "Pen & Paper Based (OMR Sheet)",
      totalQuestions: 200,
      questionsToAttempt: 180,
      duration: "180 minutes (3 hours)",
      totalMarks: 720,
      marking: "+4 for correct, -1 for incorrect, 0 for unattempted",
      sections: [
        { name: "Physics", questions: 50, toAttempt: 45, marks: 180, detail: "Section A: 35 mandatory MCQs + Section B: 15 MCQs, attempt any 10" },
        { name: "Chemistry", questions: 50, toAttempt: 45, marks: 180, detail: "Section A: 35 mandatory MCQs + Section B: 15 MCQs, attempt any 10" },
        { name: "Botany", questions: 50, toAttempt: 45, marks: 180, detail: "Section A: 35 mandatory MCQs + Section B: 15 MCQs, attempt any 10" },
        { name: "Zoology", questions: 50, toAttempt: 45, marks: 180, detail: "Section A: 35 mandatory MCQs + Section B: 15 MCQs, attempt any 10" }
      ],
      languages: ["English", "Hindi", "Assamese", "Bengali", "Gujarati", "Kannada", "Malayalam", "Marathi", "Odia", "Punjabi", "Tamil", "Telugu", "Urdu"]
    },
    applicationFee: {
      general: "₹1700 (UR/OBC-NCL/EWS)",
      scSt: "₹1000 (SC/ST/PwD/Transgender)",
      outsideIndia: "₹9500"
    },
    jobOpportunities: [
      { title: "Doctor (MBBS)", salary: "₹8-50 LPA", companies: "AIIMS, Apollo, Fortis, Max Healthcare, Medanta", description: "Practice as a general physician in hospitals or clinics" },
      { title: "Surgeon/Specialist (After MD/MS)", salary: "₹25 LPA - ₹1.5+ Cr", companies: "Private Practice, Corporate Hospitals", description: "Specialize in surgery, cardiology, neurology, orthopedics, etc." },
      { title: "Medical Researcher", salary: "₹10-35 LPA", companies: "ICMR, WHO, Pfizer, Novartis, AIIMS", description: "Research diseases, develop treatments, and conduct clinical trials" },
      { title: "Dentist (BDS)", salary: "₹4-25 LPA", companies: "Dental Clinics, Hospitals, Own Practice", description: "Dental care, oral surgery, and cosmetic dentistry" },
      { title: "Government Doctor", salary: "₹60,000 - ₹2 LPA/month + perks", companies: "Government Hospitals, PHCs, CHCs", description: "Serve in government healthcare facilities with job security" },
      { title: "AYUSH Practitioner", salary: "₹4-15 LPA", companies: "AYUSH Hospitals, Own Practice", description: "Practice Ayurveda, Homeopathy, Unani, or Siddha medicine" }
    ],
    collegePackages: [
      { college: "AIIMS (All India - 23 Institutes)", avgPackage: "Government Service", highestPackage: "Private Practice / Super-Speciality ₹1.5 Cr+" },
      { college: "JIPMER Puducherry", avgPackage: "Government Service", highestPackage: "Specialist ₹80 LPA+" },
      { college: "CMC Vellore", avgPackage: "₹12-18 LPA", highestPackage: "₹50+ LPA" },
      { college: "AFMC Pune", avgPackage: "Armed Forces Service", highestPackage: "Specialist + Military Benefits" },
      { college: "King George's Medical University", avgPackage: "₹8-15 LPA", highestPackage: "₹40+ LPA" }
    ],
    topColleges: ["AIIMS Delhi", "JIPMER Puducherry", "CMC Vellore", "AFMC Pune", "Maulana Azad Medical College", "King George's Medical University", "Grant Medical College Mumbai", "Kasturba Medical College Manipal", "Lady Hardinge Medical College Delhi", "Seth GS Medical College Mumbai"],
    whyPrepare: [
      "Only exam for MBBS/BDS admission in India (mandatory since 2016)",
      "Access to 1,10,000+ MBBS seats in 2319+ medical colleges across India",
      "Entry to 23 AIIMS institutes and JIPMER through same exam",
      "15% All India Quota + 85% State Quota seats",
      "Noble profession with highest respect & lifelong job security",
      "Pathway to super-specialization with earning potential of ₹1 Cr+ annually"
    ],
    preparationTips: [
      "Focus heavily on Biology (50% weightage - 360 marks out of 720)",
      "NCERT is the Bible - read line by line including diagrams, flowcharts, and tables",
      "Practice from MTG, Objective NCERT at Your Fingertips, and DC Pandey (Physics)",
      "Attempt previous 10 years papers with detailed analysis of weak areas",
      "Section B (optional questions) gives strategic advantage - prepare well for it",
      "Revision is key - make short notes and revise Biology diagrams regularly"
    ],
    previousCutoff: {
      general: "720-164 (2024)",
      obc: "163-129 (2024)",
      sc: "163-129 (2024)",
      st: "163-129 (2024)"
    },
    importantDates2026: {
      registration: "February 2026 (expected)",
      lastDateToApply: "March 2026 (expected)",
      admitCard: "April 2026 (expected)",
      examDate: "First Sunday of May 2026 (expected - 3 May 2026)",
      answerKey: "May 2026 (within 2 weeks of exam)",
      resultDate: "June 2026",
      counselling: "MCC counselling July-October 2026"
    }
  },
  "upsc_cse": {
    fullName: "UPSC Civil Services Examination",
    conductedBy: "Union Public Service Commission",
    overview: "The most prestigious examination in India for recruitment to various civil services including IAS, IPS, IFS, and allied services. It shapes the administrative backbone of the country.",
    examFrequency: "Once a year",
    totalCandidates: "10+ Lakh annually",
    eligibility: {
      age: "21-32 years (relaxation for SC/ST/OBC)",
      qualification: "Bachelor's degree from recognized university",
      minimumMarks: "No minimum percentage required",
      attempts: "General: 6, OBC: 9, SC/ST: Unlimited (till age limit)",
      yearOfPassing: "Degree completed or appearing in final year"
    },
    examPattern: {
      mode: "Pen & Paper + Interview",
      stages: "Prelims → Mains → Interview",
      totalMarks: "2025 marks (Mains + Interview)",
      marking: "Prelims: +2 correct, -0.66 incorrect",
      sections: [
        { name: "Prelims Paper 1 (GS)", questions: 100, marks: 200 },
        { name: "Prelims Paper 2 (CSAT)", questions: 80, marks: 200 },
        { name: "Mains (9 Papers)", questions: "Descriptive", marks: 1750 },
        { name: "Interview", questions: "Personality Test", marks: 275 }
      ]
    },
    jobOpportunities: [
      { title: "IAS Officer", salary: "₹56,100 - ₹2,50,000/month + perks", companies: "District Administration, State/Central Govt", description: "Highest administrative position in civil services" },
      { title: "IPS Officer", salary: "₹56,100 - ₹2,25,000/month + perks", companies: "Police Force, Intelligence", description: "Lead police force and maintain law & order" },
      { title: "IFS Officer", salary: "₹56,100 - ₹2,00,000/month + perks", companies: "Ministry of External Affairs", description: "Represent India in foreign countries" },
      { title: "IRS Officer", salary: "₹56,100 - ₹2,00,000/month", companies: "Income Tax, Customs", description: "Handle taxation and revenue matters" },
    ],
    topColleges: ["LBSNAA Mussoorie (Training)", "SVP NPA Hyderabad (IPS)", "FSI Hyderabad (IFS)"],
    whyPrepare: [
      "Become a nation builder & policy maker",
      "Highest respect & prestige in society",
      "Job security for lifetime",
      "Power to bring positive change",
      "Excellent pay, perks & housing"
    ],
    preparationTips: [
      "Read The Hindu/Indian Express daily",
      "Make notes from standard books",
      "Join test series for Prelims & Mains",
      "Focus on Answer Writing practice"
    ],
    importantDates2026: {
      notification: "February 2026",
      prelimsExam: "May 2026",
      mainsExam: "September 2026"
    }
  },
  "cat": {
    fullName: "Common Admission Test",
    conductedBy: "IIMs (Rotating basis)",
    overview: "CAT is the premier MBA entrance exam in India, opening doors to IIMs and top B-schools. It tests quantitative, verbal, and logical abilities required for management education.",
    examFrequency: "Once a year (November)",
    totalCandidates: "2.5+ Lakh annually",
    jobOpportunities: [
      { title: "Management Consultant", salary: "₹20-50 LPA", companies: "McKinsey, BCG, Bain" },
      { title: "Investment Banker", salary: "₹15-60 LPA", companies: "Goldman Sachs, JP Morgan" },
      { title: "Product Manager", salary: "₹25-50 LPA", companies: "Google, Amazon, Microsoft" },
      { title: "Brand Manager", salary: "₹15-35 LPA", companies: "HUL, P&G, ITC" },
    ],
    topColleges: ["IIM Ahmedabad", "IIM Bangalore", "IIM Calcutta", "IIM Lucknow", "IIM Indore", "XLRI Jamshedpur", "FMS Delhi"],
    whyPrepare: [
      "Entry to top IIMs",
      "Average package ₹25+ LPA at top IIMs",
      "Global career opportunities",
      "Leadership & entrepreneurship skills",
      "Extensive alumni network"
    ],
    preparationTips: [
      "Focus on speed and accuracy",
      "Master Quant basics thoroughly",
      "Read extensively for RC",
      "Take 100+ full-length mocks"
    ],
    importantDates2026: {
      registration: "August 2025",
      examDate: "November 2025",
      resultDate: "December 2025"
    }
  },
  "gate": {
    fullName: "Graduate Aptitude Test in Engineering",
    conductedBy: "IISc Bangalore & 7 IITs",
    overview: "GATE is for admission to M.Tech/ME programs in IITs, IISc, NITs and for PSU recruitment. It's essential for engineering graduates seeking higher education or PSU jobs.",
    examFrequency: "Once a year (February)",
    totalCandidates: "8+ Lakh annually",
    jobOpportunities: [
      { title: "PSU Engineer", salary: "₹8-15 LPA", companies: "ONGC, IOCL, NTPC, BHEL, GAIL" },
      { title: "Research Scholar", salary: "₹31,000-35,000/month stipend", companies: "IITs, IISc, NITs" },
      { title: "Scientist", salary: "₹8-20 LPA", companies: "ISRO, DRDO, BARC" },
    ],
    topColleges: ["IISc Bangalore", "IIT Bombay", "IIT Delhi", "IIT Madras", "IIT Kanpur"],
    whyPrepare: [
      "Direct entry to IITs for M.Tech",
      "High-paying PSU jobs",
      "Research opportunities with stipend",
      "Valid for 3 years",
      "Opens PhD opportunities"
    ],
    importantDates2026: {
      registration: "August - October 2025",
      examDate: "7 February 2026",
      resultDate: "19 March 2026"
    }
  },
  "ssc_cgl": {
    fullName: "SSC Combined Graduate Level Examination",
    conductedBy: "Staff Selection Commission",
    overview: "SSC CGL recruits for various Group B and Group C posts in ministries, departments, and organizations under the Government of India.",
    examFrequency: "Once a year",
    totalCandidates: "30+ Lakh annually",
    jobOpportunities: [
      { title: "Income Tax Inspector", salary: "₹44,900 - ₹1,42,400", companies: "CBDT" },
      { title: "Assistant Audit Officer", salary: "₹47,600 - ₹1,51,100", companies: "CAG Office" },
      { title: "Assistant Section Officer", salary: "₹44,900 - ₹1,42,400", companies: "Central Secretariat" },
      { title: "Sub Inspector (CBI)", salary: "₹44,900 - ₹1,42,400", companies: "CBI" },
    ],
    topColleges: ["Government Service - No College Required"],
    whyPrepare: [
      "Prestigious government jobs",
      "Job security & pension benefits",
      "Work-life balance",
      "Various post preferences",
      "Promotions & growth"
    ],
    importantDates2026: {
      notification: "March 2026",
      tier1Exam: "June-July 2026",
      resultDate: "August 2026"
    }
  },
  "jee_advanced": {
    fullName: "Joint Entrance Examination (Advanced)",
    conductedBy: "IITs (Rotating basis)",
    overview: "JEE Advanced is the gateway to the prestigious Indian Institutes of Technology (IITs). Only top 2.5 lakh JEE Main qualifiers can appear for this exam, making it one of the world's toughest entrance exams.",
    examFrequency: "Once a year (May)",
    totalCandidates: "2.5 Lakh annually",
    eligibility: {
      age: "Born on or after October 1, 2001 (5 years relaxation for SC/ST/PwD)",
      qualification: "Must be among top 2,50,000 in JEE Main (including all categories)",
      minimumMarks: "75% in Class 12th (65% for SC/ST) OR Top 20 percentile",
      attempts: "Maximum 2 consecutive years",
      yearOfPassing: "Class 12 passed in 2025 or 2026 only"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      papers: "2 Papers (Paper 1 & Paper 2) - Both compulsory",
      totalQuestions: "54 (varies by paper)",
      duration: "3 hours each paper (total 6 hours)",
      marking: "+3/+4 for correct, -1 for incorrect (varies)",
      sections: [
        { name: "Physics", questions: 18, marks: 60 },
        { name: "Chemistry", questions: 18, marks: 60 },
        { name: "Mathematics", questions: 18, marks: 60 }
      ]
    },
    jobOpportunities: [
      { title: "Software Engineer", salary: "₹15-50 LPA", companies: "Google, Microsoft, Amazon, Apple", description: "Top tech companies recruit directly from IIT campuses" },
      { title: "Quantitative Analyst", salary: "₹25-75 LPA", companies: "Goldman Sachs, Morgan Stanley, Tower Research", description: "High-paying finance roles for math-strong candidates" },
      { title: "Data Scientist", salary: "₹15-40 LPA", companies: "Meta, Netflix, Uber", description: "ML/AI roles with leading companies" },
      { title: "Product Manager", salary: "₹20-60 LPA", companies: "Google, Microsoft, Flipkart", description: "Lead product development at top companies" },
      { title: "Research Scientist", salary: "₹12-30 LPA", companies: "ISRO, DRDO, Microsoft Research", description: "R&D opportunities" },
    ],
    collegePackages: [
      { college: "IIT Bombay", avgPackage: "₹21-25 LPA", highestPackage: "₹2.4+ Cr (International)" },
      { college: "IIT Delhi", avgPackage: "₹20-24 LPA", highestPackage: "₹2+ Cr" },
      { college: "IIT Madras", avgPackage: "₹18-22 LPA", highestPackage: "₹1.8+ Cr" },
      { college: "IIT Kanpur", avgPackage: "₹18-21 LPA", highestPackage: "₹1.5+ Cr" },
      { college: "IIT Kharagpur", avgPackage: "₹16-20 LPA", highestPackage: "₹1.4+ Cr" }
    ],
    topColleges: ["IIT Bombay", "IIT Delhi", "IIT Madras", "IIT Kanpur", "IIT Kharagpur", "IIT Roorkee", "IIT Guwahati"],
    whyPrepare: [
      "Entry to world's best engineering colleges (IITs)",
      "Average package ₹20+ LPA at top IITs",
      "Global career opportunities - 100% placement",
      "Reputed faculty & research facilities",
      "Unmatched alumni network worldwide"
    ],
    preparationTips: [
      "Clear JEE Main first (top 2.5 lakh rank needed)",
      "Focus on conceptual depth over quantity of problems",
      "Practice previous 20 years papers thoroughly",
      "Master multi-concept problems - JEE Advanced loves them",
      "Time management is critical - practice under time pressure"
    ],
    importantDates2026: {
      registration: "23 April - 2 May 2026",
      examDate: "17 May 2026",
      resultDate: "1 June 2026"
    }
  },
  "bitsat": {
    fullName: "BITS Admission Test",
    conductedBy: "Birla Institute of Technology and Science",
    overview: "BITSAT is the entrance exam for BITS Pilani, Goa, and Hyderabad campuses. Known for its online adaptive format and high-quality engineering education equivalent to top IITs.",
    examFrequency: "Twice a year (May & June)",
    totalCandidates: "2+ Lakh annually",
    eligibility: {
      age: "No age limit",
      qualification: "Class 12th with Physics, Chemistry & Mathematics (minimum 75% aggregate)",
      minimumMarks: "75% aggregate in PCM with minimum 60% in each subject",
      attempts: "Only in the year of Class 12 or year after",
      yearOfPassing: "2025 or 2026"
    },
    examPattern: {
      mode: "Computer Based Test (CBT) - Adaptive",
      totalQuestions: 130,
      duration: "180 minutes (3 hours)",
      marking: "+3 for correct, -1 for incorrect, No negative for unattempted",
      sections: [
        { name: "Physics", questions: 40, marks: 120 },
        { name: "Chemistry", questions: 40, marks: 120 },
        { name: "English & Logical Reasoning", questions: 25, marks: 75 },
        { name: "Mathematics", questions: 45, marks: 135 }
      ]
    },
    jobOpportunities: [
      { title: "Software Engineer", salary: "₹12-45 LPA", companies: "Google, Microsoft, Adobe, Samsung", description: "Top tech placements with excellent packages" },
      { title: "Finance/Consulting", salary: "₹15-40 LPA", companies: "McKinsey, BCG, Goldman Sachs", description: "Premium finance and consulting roles" },
      { title: "Product Manager", salary: "₹18-50 LPA", companies: "Google, Amazon, Flipkart", description: "Lead product development at unicorns" },
      { title: "Entrepreneur", salary: "Varies", companies: "200+ BITS alumni startups", description: "Strong startup ecosystem and support" },
    ],
    collegePackages: [
      { college: "BITS Pilani", avgPackage: "₹18-22 LPA", highestPackage: "₹1.5+ Cr" },
      { college: "BITS Goa", avgPackage: "₹14-18 LPA", highestPackage: "₹1+ Cr" },
      { college: "BITS Hyderabad", avgPackage: "₹12-16 LPA", highestPackage: "₹80+ LPA" }
    ],
    topColleges: ["BITS Pilani", "BITS Goa", "BITS Hyderabad"],
    whyPrepare: [
      "No JEE dependency - direct admission based on BITSAT",
      "Excellent placements (100% in top branches)",
      "Practice School - 6 months real industry experience",
      "Strong alumni network globally",
      "Dual degree option available"
    ],
    preparationTips: [
      "Speed is crucial - 3 hours for 130 questions (1.4 min/question)",
      "Focus on English & Logical Reasoning (unique to BITSAT)",
      "No negative for unattempted - attempt strategically",
      "Practice with timed online tests",
      "Bonus questions available if you finish early"
    ],
    importantDates2026: {
      registration: "15 December 2025 - 16 March 2026",
      session1Exam: "15-17 April 2026",
      session2Exam: "June 2026"
    }
  },
  "nda": {
    fullName: "National Defence Academy Examination",
    conductedBy: "Union Public Service Commission (UPSC)",
    overview: "NDA is the entry point to join Indian Armed Forces (Army, Navy, Air Force) as an officer. Candidates join the prestigious National Defence Academy, Khadakwasla for training.",
    examFrequency: "Twice a year",
    totalCandidates: "5+ Lakh annually",
    jobOpportunities: [
      { title: "Army Officer", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Army" },
      { title: "Naval Officer", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Navy" },
      { title: "Air Force Officer", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Air Force" },
    ],
    topColleges: ["National Defence Academy, Khadakwasla", "Indian Military Academy, Dehradun", "Indian Naval Academy, Ezhimala"],
    whyPrepare: [
      "Serve the nation with honor",
      "Adventurous & prestigious career",
      "Quality education at no cost",
      "Excellent pay & perks",
      "Early leadership training"
    ],
    preparationTips: [
      "Focus on Mathematics (300 marks)",
      "General Ability covers English, GK, Science",
      "Physical fitness is crucial for SSB",
      "Practice previous year papers"
    ],
    importantDates2026: {
      notification: "December 2025",
      examDate: "April 2026",
      ssbInterview: "June-August 2026"
    }
  },
  "clat": {
    fullName: "Common Law Admission Test",
    conductedBy: "Consortium of NLUs",
    overview: "CLAT is the gateway to 24 National Law Universities (NLUs) in India. It's the most prestigious law entrance exam for 5-year integrated LLB and LLM programs.",
    examFrequency: "Once a year (December)",
    totalCandidates: "80,000+ annually",
    jobOpportunities: [
      { title: "Corporate Lawyer", salary: "₹15-50 LPA", companies: "AZB, Cyril Amarchand, Khaitan & Co" },
      { title: "Litigation Lawyer", salary: "₹8-30 LPA", companies: "Supreme Court, High Courts" },
      { title: "Legal Counsel", salary: "₹12-40 LPA", companies: "Google, Amazon, Reliance" },
      { title: "Judge (after experience)", salary: "₹1-2.5 Lakh/month", companies: "Judiciary" },
    ],
    topColleges: ["NLSIU Bangalore", "NALSAR Hyderabad", "NLU Delhi", "NUJS Kolkata", "NLU Jodhpur"],
    whyPrepare: [
      "Entry to India's best law schools",
      "Corporate law packages at par with IIMs",
      "Path to judiciary & public service",
      "Global career opportunities"
    ],
    preparationTips: [
      "Read newspapers daily for current affairs",
      "Focus on comprehension-based questions",
      "Practice legal reasoning passages",
      "Time management is crucial"
    ],
    importantDates2026: {
      notification: "July 2025",
      examDate: "December 2025",
      resultDate: "January 2026"
    }
  },
  "ibps_po": {
    fullName: "IBPS Probationary Officer Examination",
    conductedBy: "Institute of Banking Personnel Selection",
    overview: "IBPS PO is for recruitment of Probationary Officers in 11 Public Sector Banks. It's one of India's most sought-after banking exams with excellent career growth.",
    examFrequency: "Once a year",
    totalCandidates: "30+ Lakh annually",
    jobOpportunities: [
      { title: "Probationary Officer", salary: "₹52,000 - ₹65,000/month", companies: "SBI, PNB, BOB, Canara Bank" },
      { title: "Branch Manager", salary: "₹80,000 - ₹1,20,000/month", companies: "Public Sector Banks" },
      { title: "Regional Manager", salary: "₹1,20,000+/month", companies: "Public Sector Banks" },
    ],
    topColleges: ["Banking Institute - Training by Banks"],
    whyPrepare: [
      "Prestigious banking career",
      "Job security & pension",
      "Regular promotions",
      "Work-life balance",
      "Pan-India postings"
    ],
    preparationTips: [
      "Master quantitative aptitude shortcuts",
      "Practice reasoning daily",
      "Stay updated with banking awareness",
      "Attempt sectional tests regularly"
    ],
    importantDates2026: {
      notification: "August 2026",
      prelimsExam: "October 2026",
      mainsExam: "November 2026"
    }
  },
  "cuet": {
    fullName: "Common University Entrance Test",
    conductedBy: "National Testing Agency (NTA)",
    overview: "CUET is the common entrance exam for admission to undergraduate programs in 50+ Central Universities, State Universities, and Deemed Universities across India.",
    examFrequency: "Once a year (May)",
    totalCandidates: "15+ Lakh annually",
    jobOpportunities: [
      { title: "Depends on Course", salary: "Varies by course", companies: "All industries" },
      { title: "Central Govt Jobs", salary: "₹35,000 - ₹1,50,000", companies: "UPSC, SSC, Railways" },
    ],
    topColleges: ["Delhi University", "BHU", "JNU", "Jamia Millia Islamia", "AMU"],
    whyPrepare: [
      "Single exam for 50+ universities",
      "Merit-based transparent admission",
      "Access to top central universities",
      "Wide range of courses"
    ],
    preparationTips: [
      "Focus on NCERT thoroughly",
      "Practice domain-specific subjects",
      "Time management in exam",
      "Attempt mock tests regularly"
    ],
    importantDates2026: {
      registration: "3 January - 30 January 2026",
      examDate: "11 May - 31 May 2026",
      resultDate: "30 July 2026"
    }
  },
  "neet_pg": {
    fullName: "National Eligibility cum Entrance Test (PG)",
    conductedBy: "National Board of Examinations (NBE)",
    overview: "NEET PG is for admission to MD/MS/PG Diploma courses in medical colleges. It's mandatory for MBBS graduates seeking specialization in any branch of medicine.",
    examFrequency: "Once a year (March)",
    totalCandidates: "2+ Lakh annually",
    eligibility: {
      age: "No upper age limit",
      qualification: "MBBS degree from recognized university",
      minimumMarks: "50% marks in MBBS (45% for SC/ST/OBC, 40% for PwD)",
      attempts: "No limit",
      yearOfPassing: "Completed internship by March 31, 2026"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: 200,
      duration: "210 minutes (3.5 hours)",
      marking: "+4 for correct, -1 for incorrect",
      sections: [
        { name: "Pre-Clinical Subjects", questions: 50, marks: 200 },
        { name: "Para-Clinical Subjects", questions: 50, marks: 200 },
        { name: "Clinical Subjects", questions: 100, marks: 400 }
      ]
    },
    jobOpportunities: [
      { title: "Specialist Doctor (MD/MS)", salary: "₹25 LPA - ₹1+ Cr", companies: "AIIMS, Apollo, Fortis, Private Practice", description: "Practice as a specialist in hospitals" },
      { title: "Super-Specialist (DM/MCh)", salary: "₹50 LPA - ₹2+ Cr", companies: "Top Hospitals, Private Practice", description: "Specialized surgery and medical treatment" },
      { title: "Medical Professor", salary: "₹15-30 LPA", companies: "Medical Colleges, AIIMS", description: "Teach and research at medical institutions" },
    ],
    topColleges: ["AIIMS Delhi", "JIPMER", "PGI Chandigarh", "CMC Vellore", "KEM Mumbai"],
    whyPrepare: [
      "Mandatory for medical specialization",
      "High earning potential as specialist",
      "Respect and job security",
      "Opportunity for super-specialization"
    ],
    preparationTips: [
      "Revise all MBBS subjects systematically",
      "Focus on clinical subjects (50% weightage)",
      "Solve previous 10 years papers",
      "Join a good test series"
    ],
    importantDates2026: {
      registration: "January 2026",
      examDate: "March 2026",
      resultDate: "April 2026"
    }
  },
  "iat": {
    fullName: "IISER Aptitude Test",
    conductedBy: "Indian Institutes of Science Education and Research",
    overview: "IAT is for admission to 5-year BS-MS dual degree program at IISERs. It's for students passionate about pure sciences - Physics, Chemistry, Biology, Mathematics.",
    examFrequency: "Once a year (June)",
    totalCandidates: "60,000+ annually",
    eligibility: {
      age: "No age limit",
      qualification: "Class 12th with Science (PCM/PCB)",
      minimumMarks: "60% in Class 12 (55% for SC/ST)",
      attempts: "In the year of Class 12 or year after",
      yearOfPassing: "2025 or 2026"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: 60,
      duration: "180 minutes (3 hours)",
      marking: "+3 for correct, -1 for incorrect",
      sections: [
        { name: "Biology", questions: 15, marks: 45 },
        { name: "Chemistry", questions: 15, marks: 45 },
        { name: "Mathematics", questions: 15, marks: 45 },
        { name: "Physics", questions: 15, marks: 45 }
      ]
    },
    jobOpportunities: [
      { title: "Research Scientist", salary: "₹8-25 LPA", companies: "IISER, IISc, TIFR, NCBS", description: "Research in pure sciences" },
      { title: "Data Scientist", salary: "₹10-40 LPA", companies: "Google, Microsoft, Quant firms", description: "Apply scientific rigor to data analysis" },
      { title: "Professor", salary: "₹12-30 LPA", companies: "IISERs, IITs, Universities", description: "Teaching and research in academia" },
    ],
    collegePackages: [
      { college: "IISER Pune", avgPackage: "₹10-15 LPA", highestPackage: "₹40+ LPA" },
      { college: "IISER Kolkata", avgPackage: "₹8-12 LPA", highestPackage: "₹30+ LPA" },
      { college: "IISER Mohali", avgPackage: "₹8-12 LPA", highestPackage: "₹35+ LPA" }
    ],
    topColleges: ["IISER Pune", "IISER Kolkata", "IISER Mohali", "IISER Bhopal", "IISER Thiruvananthapuram", "IISER Berhampur", "IISER Tirupati"],
    whyPrepare: [
      "Strong research environment",
      "Integrated BS-MS degree (5 years)",
      "Excellent faculty and infrastructure",
      "Funded PhD opportunities abroad",
      "Strong focus on pure sciences"
    ],
    preparationTips: [
      "Strong conceptual understanding needed",
      "Practice analytical reasoning questions",
      "Focus on NCERT + higher-level problems",
      "Solve previous years' papers"
    ],
    importantDates2026: {
      registration: "April - May 2026",
      examDate: "June 2026",
      resultDate: "July 2026"
    }
  },
  "nest": {
    fullName: "National Entrance Screening Test",
    conductedBy: "National Institute of Science Education and Research (NISER) & University of Mumbai - DAE CEBS",
    overview: "NEST is for admission to 5-year Integrated MSc programs at NISER Bhubaneswar and UM-DAE CEBS Mumbai. Focused on pure sciences with strong research orientation.",
    examFrequency: "Once a year (June)",
    totalCandidates: "50,000+ annually",
    eligibility: {
      age: "Born on or after August 1, 2002",
      qualification: "Class 12th with Science",
      minimumMarks: "60% aggregate (55% for SC/ST/PwD)",
      attempts: "In the year of Class 12 or year after",
      yearOfPassing: "2025 or 2026"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: "Section-wise varying",
      duration: "200 minutes (3 hrs 20 mins)",
      marking: "Varies by section",
      sections: [
        { name: "General (Mandatory)", questions: 20, marks: 30 },
        { name: "Biology (Choose any 2)", questions: 30, marks: 80 },
        { name: "Chemistry (Choose any 2)", questions: 30, marks: 80 },
        { name: "Mathematics (Choose any 2)", questions: 30, marks: 80 },
        { name: "Physics (Choose any 2)", questions: 30, marks: 80 }
      ]
    },
    jobOpportunities: [
      { title: "Research Scientist", salary: "₹8-20 LPA", companies: "BARC, TIFR, NISER", description: "Research at premier science institutions" },
      { title: "PhD Scholar", salary: "₹31,000-35,000/month", companies: "Top global universities", description: "Funded PhD at MIT, Stanford, etc." },
      { title: "Scientist", salary: "₹10-25 LPA", companies: "ISRO, DRDO, DAE", description: "Government research organizations" },
    ],
    topColleges: ["NISER Bhubaneswar", "UM-DAE CEBS Mumbai"],
    whyPrepare: [
      "Excellent research infrastructure",
      "Small batch sizes (personalized attention)",
      "Strong DAE connection for careers",
      "Well-funded research opportunities"
    ],
    preparationTips: [
      "Choose 2 subjects you're strongest in",
      "General section tests logical reasoning",
      "Practice NCERT + Olympiad level problems",
      "Focus on conceptual understanding"
    ],
    importantDates2026: {
      registration: "January - April 2026",
      examDate: "June 2026",
      resultDate: "July 2026"
    }
  },
  "iisc_bs": {
    fullName: "IISc BS Program Admission",
    conductedBy: "Indian Institute of Science, Bangalore",
    overview: "IISc offers a 4-year BS program in science disciplines. Admission is through JEE Main, NEET, or KVPY. It's India's top-ranked research institution.",
    examFrequency: "Based on JEE/NEET/KVPY scores",
    totalCandidates: "Limited seats (competitive)",
    eligibility: {
      age: "No age limit",
      qualification: "Class 12th with Science",
      minimumMarks: "60% in Class 12 (55% for SC/ST)",
      attempts: "Based on valid JEE/NEET/KVPY score",
      yearOfPassing: "2025 or 2026"
    },
    jobOpportunities: [
      { title: "Research Scientist", salary: "₹12-30 LPA", companies: "IISc, Microsoft Research, Google Research", description: "Scientific research opportunities" },
      { title: "Data Scientist", salary: "₹15-45 LPA", companies: "Google, Amazon, Quant firms", description: "Apply scientific methods to data" },
      { title: "PhD Scholar", salary: "Fully funded", companies: "MIT, Stanford, Cambridge", description: "Pursue PhD at world's best universities" },
    ],
    topColleges: ["IISc Bangalore"],
    whyPrepare: [
      "India's #1 research institution",
      "Reputed faculty and facilities",
      "Excellent PhD/research opportunities",
      "Strong industry connections"
    ],
    preparationTips: [
      "Score well in JEE Main/NEET/KVPY",
      "Strong focus on conceptual understanding",
      "Prepare for interview round"
    ],
    importantDates2026: {
      registration: "April - May 2026",
      examDate: "Based on JEE/NEET scores",
      resultDate: "June 2026"
    }
  },
  "xat": {
    fullName: "Xavier Aptitude Test",
    conductedBy: "XLRI Jamshedpur",
    overview: "XAT is the entrance exam for XLRI Jamshedpur and 150+ other B-schools. Known for its Decision Making section, it's one of India's toughest MBA entrance exams.",
    examFrequency: "Once a year (January)",
    totalCandidates: "1+ Lakh annually",
    eligibility: {
      age: "No age limit",
      qualification: "Bachelor's degree (any discipline)",
      minimumMarks: "No minimum percentage",
      attempts: "No limit",
      yearOfPassing: "Completed or appearing in final year"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: 101,
      duration: "180 minutes (3 hours)",
      marking: "+1 for correct, -0.25 for incorrect",
      sections: [
        { name: "Verbal & Logical Ability", questions: 26, marks: 26 },
        { name: "Decision Making", questions: 21, marks: 21 },
        { name: "Quantitative Ability & DI", questions: 28, marks: 28 },
        { name: "General Knowledge", questions: 26, marks: 26 }
      ]
    },
    jobOpportunities: [
      { title: "HR Manager", salary: "₹15-35 LPA", companies: "TCS, Infosys, Deloitte", description: "XLRI known for HR specialization" },
      { title: "Management Consultant", salary: "₹20-50 LPA", companies: "McKinsey, BCG, Bain", description: "Strategic consulting roles" },
      { title: "Marketing Manager", salary: "₹15-40 LPA", companies: "HUL, P&G, ITC", description: "Brand management and marketing" },
    ],
    collegePackages: [
      { college: "XLRI Jamshedpur", avgPackage: "₹25-28 LPA", highestPackage: "₹1+ Cr" },
      { college: "IMT Ghaziabad", avgPackage: "₹14-16 LPA", highestPackage: "₹45+ LPA" }
    ],
    topColleges: ["XLRI Jamshedpur", "IMT Ghaziabad", "XIMB Bhubaneswar", "GIM Goa", "TAPMI Manipal"],
    whyPrepare: [
      "Entry to prestigious XLRI",
      "Unique Decision Making section",
      "Excellent HR specialization",
      "150+ B-schools accept XAT",
      "Strong alumni network"
    ],
    preparationTips: [
      "Practice Decision Making cases",
      "Time management is crucial",
      "General Knowledge needs consistent effort",
      "Take full-length mocks regularly"
    ],
    importantDates2026: {
      registration: "August - November 2025",
      examDate: "January 2026",
      resultDate: "February 2026"
    }
  },
  "rrb_ntpc": {
    fullName: "RRB Non-Technical Popular Categories",
    conductedBy: "Railway Recruitment Boards",
    overview: "RRB NTPC recruits for various non-technical posts in Indian Railways like Station Master, Goods Guard, Clerk, etc. One of India's largest recruitment exams.",
    examFrequency: "As per vacancies",
    totalCandidates: "1+ Crore applications",
    eligibility: {
      age: "18-33 years (varies by post)",
      qualification: "12th pass / Graduation (depends on post)",
      minimumMarks: "No minimum percentage",
      attempts: "No limit",
      yearOfPassing: "Already passed"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      stages: "CBT 1 → CBT 2 → Typing/Computer Test",
      totalQuestions: 100,
      duration: "90 minutes",
      marking: "+1 for correct, -1/3 for incorrect",
      sections: [
        { name: "General Awareness", questions: 40, marks: 40 },
        { name: "Mathematics", questions: 30, marks: 30 },
        { name: "General Intelligence & Reasoning", questions: 30, marks: 30 }
      ]
    },
    jobOpportunities: [
      { title: "Station Master", salary: "₹35,400 - ₹1,12,400", companies: "Indian Railways", description: "Manage railway station operations" },
      { title: "Goods Guard", salary: "₹35,400 - ₹1,12,400", companies: "Indian Railways", description: "Ensure safe transportation of goods" },
      { title: "Senior Clerk", salary: "₹25,500 - ₹81,100", companies: "Indian Railways", description: "Administrative and clerical work" },
    ],
    topColleges: ["Indian Railway Training Institutes"],
    whyPrepare: [
      "Job security in Indian Railways",
      "Good salary with benefits",
      "Railway passes for family",
      "Pension and medical benefits",
      "Career growth opportunities"
    ],
    preparationTips: [
      "Focus on General Awareness",
      "Practice basic mathematics",
      "Solve reasoning puzzles daily",
      "Attempt previous years' papers"
    ],
    importantDates2026: {
      notification: "As per vacancies",
      examDate: "To be announced",
      resultDate: "To be announced"
    }
  },
  "sbi_po": {
    fullName: "State Bank of India Probationary Officer",
    conductedBy: "State Bank of India",
    overview: "SBI PO is for recruitment of Probationary Officers in State Bank of India, India's largest bank. Highly competitive with excellent career growth.",
    examFrequency: "Once a year",
    totalCandidates: "20+ Lakh annually",
    eligibility: {
      age: "21-30 years (relaxation for reserved categories)",
      qualification: "Bachelor's degree from recognized university",
      minimumMarks: "No minimum percentage",
      attempts: "No limit (within age)",
      yearOfPassing: "Already passed or appearing in final year"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      stages: "Prelims → Mains → Interview",
      totalQuestions: "Prelims: 100, Mains: 155 + Descriptive",
      duration: "Prelims: 60 mins, Mains: 3 hrs + 30 mins",
      marking: "+1 for correct, -0.25 for incorrect",
      sections: [
        { name: "English", questions: 30, marks: 30 },
        { name: "Quantitative Aptitude", questions: 35, marks: 35 },
        { name: "Reasoning", questions: 35, marks: 35 }
      ]
    },
    jobOpportunities: [
      { title: "Probationary Officer", salary: "₹52,000 - ₹65,000/month", companies: "State Bank of India", description: "Start banking career at India's largest bank" },
      { title: "Branch Manager", salary: "₹80,000 - ₹1,20,000/month", companies: "SBI Branches", description: "Manage branch operations" },
      { title: "Chief Manager", salary: "₹1,20,000+/month", companies: "SBI Regional Offices", description: "Senior management roles" },
    ],
    topColleges: ["SBI Staff Training Centres"],
    whyPrepare: [
      "India's largest and most trusted bank",
      "Excellent salary and perks",
      "Job security and pension",
      "Career growth to GM level",
      "Work-life balance"
    ],
    preparationTips: [
      "Master quantitative techniques",
      "Practice puzzles and seating arrangement",
      "Read newspapers for banking awareness",
      "Take mock tests regularly"
    ],
    importantDates2026: {
      notification: "October 2026",
      prelimsExam: "November 2026",
      mainsExam: "December 2026"
    }
  },
  "rbi_grade_b": {
    fullName: "Reserve Bank of India Grade B Officer",
    conductedBy: "Reserve Bank of India",
    overview: "RBI Grade B is for recruitment of officers in India's central bank. One of the most prestigious banking exams with excellent pay and growth.",
    examFrequency: "Once a year",
    totalCandidates: "5+ Lakh annually",
    eligibility: {
      age: "21-30 years (relaxation for reserved categories)",
      qualification: "Bachelor's degree with 60% (55% for SC/ST/PwD)",
      minimumMarks: "60% in graduation",
      attempts: "6 for General, 9 for OBC, unlimited for SC/ST",
      yearOfPassing: "Already passed"
    },
    examPattern: {
      mode: "Computer Based Test (CBT) + Interview",
      stages: "Phase 1 → Phase 2 → Interview",
      totalQuestions: "Phase 1: 200, Phase 2: Descriptive",
      duration: "Phase 1: 120 mins, Phase 2: 90 mins each",
      marking: "+1 for correct, -0.25 for incorrect",
      sections: [
        { name: "General Awareness", questions: 80, marks: 80 },
        { name: "Quantitative Aptitude", questions: 30, marks: 30 },
        { name: "English Language", questions: 30, marks: 30 },
        { name: "Reasoning", questions: 60, marks: 60 }
      ]
    },
    jobOpportunities: [
      { title: "Grade B Officer", salary: "₹77,208/month (approx.)", companies: "RBI", description: "Work at India's central bank" },
      { title: "Deputy General Manager", salary: "₹1,30,000+/month", companies: "RBI Offices", description: "Senior management position" },
      { title: "Executive Director", salary: "₹2,00,000+/month", companies: "RBI Central Office", description: "Top management roles" },
    ],
    topColleges: ["RBI Staff College, Chennai", "RBI Training Centres"],
    whyPrepare: [
      "India's central bank - highest prestige",
      "Best salary among banking jobs",
      "Excellent perks (housing, car, etc.)",
      "Job security for life",
      "Policy-making involvement"
    ],
    preparationTips: [
      "Strong focus on Economics & Finance",
      "Read Economic Survey and RBI Reports",
      "Practice essay writing for Phase 2",
      "Current affairs is crucial"
    ],
    importantDates2026: {
      notification: "March 2026",
      phase1Exam: "May 2026",
      phase2Exam: "June 2026"
    }
  },
  "cds": {
    fullName: "Combined Defence Services Examination",
    conductedBy: "Union Public Service Commission (UPSC)",
    overview: "CDS is for recruitment to Indian Military Academy (IMA), Naval Academy, Air Force Academy, and Officers Training Academy. For graduates aspiring to join defence forces.",
    examFrequency: "Twice a year",
    totalCandidates: "4+ Lakh annually",
    eligibility: {
      age: "19.5-25 years (varies by academy)",
      qualification: "Bachelor's degree (Engineering for Technical entries)",
      minimumMarks: "No minimum percentage",
      attempts: "Within age limit",
      yearOfPassing: "Completed or appearing in final year"
    },
    examPattern: {
      mode: "Pen & Paper Based (OMR)",
      totalQuestions: 300,
      duration: "120 minutes per paper",
      marking: "+1 for correct, -1/3 for incorrect",
      sections: [
        { name: "English", questions: 120, marks: 100 },
        { name: "General Knowledge", questions: 120, marks: 100 },
        { name: "Elementary Mathematics", questions: 100, marks: 100 }
      ]
    },
    jobOpportunities: [
      { title: "Army Officer", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Army", description: "Lead soldiers and serve the nation" },
      { title: "Naval Officer", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Navy", description: "Serve at sea protecting the nation" },
      { title: "Air Force Officer", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Air Force", description: "Fly and defend Indian skies" },
    ],
    topColleges: ["IMA Dehradun", "Naval Academy Ezhimala", "Air Force Academy Hyderabad", "OTA Chennai"],
    whyPrepare: [
      "Serve the nation with honor",
      "Prestigious officer rank",
      "Excellent pay and perks",
      "Adventure and leadership",
      "Respect in society"
    ],
    preparationTips: [
      "Focus on English and GK",
      "Mathematics only for IMA/AFA",
      "Physical fitness for SSB",
      "Practice previous year papers"
    ],
    importantDates2026: {
      notification: "October 2025 / June 2026",
      examDate: "February 2026 / September 2026",
      ssbInterview: "After written exam"
    }
  },
  "afcat": {
    fullName: "Air Force Common Admission Test",
    conductedBy: "Indian Air Force",
    overview: "AFCAT is the primary entry for graduates to join Indian Air Force as officers in Flying, Technical, and Ground Duty branches.",
    examFrequency: "Twice a year",
    totalCandidates: "3+ Lakh annually",
    eligibility: {
      age: "20-26 years (varies by branch)",
      qualification: "Bachelor's degree (60% in B.Tech for Technical)",
      minimumMarks: "60% in graduation (for most branches)",
      attempts: "Within age limit",
      yearOfPassing: "Completed or appearing in final year"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: 100,
      duration: "120 minutes",
      marking: "+3 for correct, -1 for incorrect",
      sections: [
        { name: "General Awareness", questions: 25, marks: 75 },
        { name: "Verbal Ability (English)", questions: 25, marks: 75 },
        { name: "Numerical Ability", questions: 15, marks: 45 },
        { name: "Reasoning & Military Aptitude", questions: 35, marks: 105 }
      ]
    },
    jobOpportunities: [
      { title: "Flying Officer (Pilot)", salary: "₹56,100 - ₹2,50,000/month", companies: "Indian Air Force", description: "Fly fighter jets, transport aircraft" },
      { title: "Technical Officer", salary: "₹56,100 - ₹2,25,000/month", companies: "IAF Technical Branch", description: "Maintain aircraft and systems" },
      { title: "Ground Duty Officer", salary: "₹56,100 - ₹2,25,000/month", companies: "IAF Administration", description: "Admin, Logistics, Accounts, Education" },
    ],
    topColleges: ["Air Force Academy, Hyderabad", "Air Force Administrative College, Coimbatore"],
    whyPrepare: [
      "Become a pilot in Indian Air Force",
      "Prestigious officer career",
      "Excellent pay and allowances",
      "Adventure and travel",
      "Early retirement with pension"
    ],
    preparationTips: [
      "Focus on reasoning and military aptitude",
      "Practice verbal ability daily",
      "Stay fit for AFSB",
      "Read about Air Force current affairs"
    ],
    importantDates2026: {
      notification: "June 2026 / December 2026",
      examDate: "August 2026 / February 2027",
      afsbInterview: "After written exam"
    }
  },
  "ailet": {
    fullName: "All India Law Entrance Test",
    conductedBy: "National Law University, Delhi",
    overview: "AILET is the entrance exam specifically for NLU Delhi, one of the top 3 law schools in India. It's separate from CLAT and highly competitive.",
    examFrequency: "Once a year (May)",
    totalCandidates: "30,000+ annually",
    eligibility: {
      age: "No age limit",
      qualification: "Class 12th from recognized board",
      minimumMarks: "50% in Class 12 (45% for SC/ST)",
      attempts: "No limit",
      yearOfPassing: "2025 or appearing in 2026"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: 150,
      duration: "90 minutes",
      marking: "+1 for correct, -0.25 for incorrect",
      sections: [
        { name: "English", questions: 35, marks: 35 },
        { name: "Current Affairs & GK", questions: 35, marks: 35 },
        { name: "Legal Aptitude", questions: 35, marks: 35 },
        { name: "Logical Reasoning", questions: 35, marks: 35 },
        { name: "Mathematics", questions: 10, marks: 10 }
      ]
    },
    jobOpportunities: [
      { title: "Corporate Lawyer", salary: "₹18-50 LPA", companies: "Tier-1 Law Firms", description: "M&A, PE, Corporate Advisory" },
      { title: "Litigator", salary: "₹10-35 LPA", companies: "Supreme Court, High Courts", description: "Argue cases in courts" },
      { title: "Legal Counsel", salary: "₹15-45 LPA", companies: "MNCs, Startups", description: "In-house legal teams" },
    ],
    collegePackages: [
      { college: "NLU Delhi", avgPackage: "₹18-22 LPA", highestPackage: "₹50+ LPA" }
    ],
    topColleges: ["National Law University, Delhi"],
    whyPrepare: [
      "Top 3 law school in India",
      "Excellent placements",
      "Located in Delhi NCR",
      "Strong alumni network"
    ],
    preparationTips: [
      "Speed is crucial (90 mins for 150 questions)",
      "Strong focus on current affairs",
      "Practice mathematics (unique to AILET)",
      "Take timed mock tests"
    ],
    importantDates2026: {
      registration: "January - April 2026",
      examDate: "May 2026",
      resultDate: "June 2026"
    }
  },
  "nid_dat": {
    fullName: "NID Design Aptitude Test",
    conductedBy: "National Institute of Design",
    overview: "NID DAT is for admission to B.Des and M.Des programs at National Institute of Design campuses. Tests creativity, design aptitude, and visual perception.",
    examFrequency: "Once a year",
    totalCandidates: "20,000+ annually",
    eligibility: {
      age: "No age limit",
      qualification: "Class 12th from any stream (for B.Des)",
      minimumMarks: "No minimum percentage",
      attempts: "No limit",
      yearOfPassing: "2025 or appearing in 2026"
    },
    examPattern: {
      mode: "Pen & Paper (Prelims) + Studio Test + Interview",
      stages: "DAT Prelims → DAT Mains → Interview",
      totalQuestions: "Drawing & Design based",
      duration: "3 hours",
      marking: "Based on creativity and design skills",
      sections: [
        { name: "Visual Perception", questions: "MCQ", marks: "NA" },
        { name: "Drawing Skills", questions: "Practical", marks: "NA" },
        { name: "Creative Thinking", questions: "Practical", marks: "NA" },
        { name: "Observation", questions: "MCQ + Practical", marks: "NA" }
      ]
    },
    jobOpportunities: [
      { title: "Product Designer", salary: "₹8-30 LPA", companies: "Apple, Google, Philips, Samsung", description: "Design consumer products" },
      { title: "UI/UX Designer", salary: "₹10-40 LPA", companies: "Google, Microsoft, Swiggy, Flipkart", description: "Design digital experiences" },
      { title: "Graphic Designer", salary: "₹6-25 LPA", companies: "Advertising agencies, Brands", description: "Visual communication design" },
    ],
    topColleges: ["NID Ahmedabad", "NID Gandhinagar", "NID Bangalore", "NID Kurukshetra", "NID Jorhat"],
    whyPrepare: [
      "India's premier design institute",
      "Top-tier design education",
      "Excellent placement opportunities",
      "Strong industry connections"
    ],
    preparationTips: [
      "Practice sketching and drawing daily",
      "Develop observation skills",
      "Study previous years' papers",
      "Build a creative portfolio"
    ],
    importantDates2026: {
      registration: "October - December 2025",
      prelimsExam: "January 2026",
      mainsExam: "March - April 2026"
    }
  },
  "nata": {
    fullName: "National Aptitude Test in Architecture",
    conductedBy: "Council of Architecture",
    overview: "NATA is for admission to B.Arch programs in architecture colleges across India. Tests drawing ability, aesthetic sensitivity, and critical thinking.",
    examFrequency: "Multiple sessions per year",
    totalCandidates: "50,000+ annually",
    eligibility: {
      age: "No age limit",
      qualification: "Class 12th with Mathematics (50% aggregate)",
      minimumMarks: "50% aggregate in 10+2",
      attempts: "No limit",
      yearOfPassing: "2025 or appearing in 2026"
    },
    examPattern: {
      mode: "Computer Based (Drawing) + Online",
      totalQuestions: "MCQ + Drawing",
      duration: "180 minutes",
      marking: "200 marks total",
      sections: [
        { name: "Drawing Test", questions: 2, marks: 125 },
        { name: "PCM/Aptitude", questions: 35, marks: 45 },
        { name: "General Aptitude", questions: 15, marks: 30 }
      ]
    },
    jobOpportunities: [
      { title: "Architect", salary: "₹6-25 LPA", companies: "Hafeez Contractor, CP Kukreja, Arcop", description: "Design buildings and spaces" },
      { title: "Urban Planner", salary: "₹8-20 LPA", companies: "Govt Town Planning, Private firms", description: "Plan cities and urban spaces" },
      { title: "Interior Designer", salary: "₹5-18 LPA", companies: "Design firms, Freelance", description: "Design interior spaces" },
    ],
    topColleges: ["SPA Delhi", "SPA Bhopal", "CEPT Ahmedabad", "JJ School of Architecture Mumbai", "Chandigarh College of Architecture"],
    whyPrepare: [
      "Mandatory for B.Arch admission",
      "Accepted by 500+ colleges",
      "Creative and fulfilling career",
      "Multiple attempts allowed"
    ],
    preparationTips: [
      "Practice freehand drawing daily",
      "Improve 3D visualization skills",
      "Study basic design principles",
      "Take mock drawing tests"
    ],
    importantDates2026: {
      registration: "January 2026",
      examDate: "April - July 2026 (multiple sessions)",
      resultDate: "Within 2 weeks of each session"
    }
  },
  "uceed": {
    fullName: "Undergraduate Common Entrance Examination for Design",
    conductedBy: "IIT Bombay",
    overview: "UCEED is for admission to B.Des programs at IIT Bombay, IIT Guwahati, IIT Delhi, IIT Hyderabad, and IIITDM Jabalpur. One of the toughest design entrance exams.",
    examFrequency: "Once a year (January)",
    totalCandidates: "15,000+ annually",
    eligibility: {
      age: "Born on or after October 1, 2001",
      qualification: "Class 12th from any stream",
      minimumMarks: "No minimum percentage",
      attempts: "Maximum 2 attempts",
      yearOfPassing: "2025 or 2026"
    },
    examPattern: {
      mode: "Computer Based Test (CBT)",
      totalQuestions: 80,
      duration: "180 minutes (3 hours)",
      marking: "+4/+3 for correct, -1 for incorrect (varies by section)",
      sections: [
        { name: "Visualization & Spatial Ability", questions: "~25", marks: "~100" },
        { name: "Observation & Design Sensitivity", questions: "~25", marks: "~100" },
        { name: "Environmental & Social Awareness", questions: "~15", marks: "~60" },
        { name: "Analytical & Logical Reasoning", questions: "~15", marks: "~60" }
      ]
    },
    jobOpportunities: [
      { title: "Product Designer", salary: "₹12-40 LPA", companies: "Apple, Google, Samsung, Philips", description: "Design physical products" },
      { title: "UX Designer", salary: "₹15-50 LPA", companies: "Google, Microsoft, Adobe, Figma", description: "Design user experiences" },
      { title: "Design Consultant", salary: "₹10-35 LPA", companies: "IDEO, Frog Design, McKinsey Design", description: "Strategic design consulting" },
    ],
    collegePackages: [
      { college: "IIT Bombay (IDC)", avgPackage: "₹15-20 LPA", highestPackage: "₹50+ LPA" },
      { college: "IIT Delhi", avgPackage: "₹12-18 LPA", highestPackage: "₹40+ LPA" }
    ],
    topColleges: ["IIT Bombay", "IIT Delhi", "IIT Guwahati", "IIT Hyderabad", "IIITDM Jabalpur"],
    whyPrepare: [
      "Design education at IITs",
      "Excellent placement record",
      "Industry-aligned curriculum",
      "Strong research opportunities"
    ],
    preparationTips: [
      "Develop visual perception skills",
      "Practice observation exercises",
      "Study environmental issues",
      "Take official mock tests"
    ],
    importantDates2026: {
      registration: "October - November 2025",
      examDate: "January 2026",
      resultDate: "March 2026"
    }
  }
};

// Map exam IDs/names to our keys - Match exact names from database
export const EXAM_KEY_MAP = {
  // Engineering - EXACT names from seed_exams.py
  'jee main': 'jee_main',           // "JEE Main"
  'jee advanced': 'jee_advanced',   // "JEE Advanced"
  'gate': 'gate',                   // "GATE"
  'bitsat': 'bitsat',               // "BITSAT"
  
  // Medical - EXACT names from seed_exams.py
  'neet ug': 'neet_ug',             // "NEET UG"
  'neet pg': 'neet_pg',             // "NEET PG"
  
  // Science - EXACT names from seed_exams.py
  'iiser aptitude test': 'iat',     // "IISER Aptitude Test"
  'nest': 'nest',                   // "NEST"
  'iisc bs': 'iisc_bs',             // "IISc BS"
  
  // Management - EXACT names from seed_exams.py
  'cat': 'cat',                     // "CAT"
  'xat': 'xat',                     // "XAT"
  
  // Government - EXACT names from seed_exams.py
  'upsc cse': 'upsc_cse',           // "UPSC CSE"
  'ssc cgl': 'ssc_cgl',             // "SSC CGL"
  'rrb ntpc': 'rrb_ntpc',           // "RRB NTPC"
  
  // Banking - EXACT names from seed_exams.py
  'ibps po': 'ibps_po',             // "IBPS PO"
  'sbi po': 'sbi_po',               // "SBI PO"
  'rbi grade b': 'rbi_grade_b',     // "RBI Grade B"
  
  // Defence - EXACT names from seed_exams.py
  'nda': 'nda',                     // "NDA"
  'cds': 'cds',                     // "CDS"
  'afcat': 'afcat',                 // "AFCAT"
  
  // Law - EXACT names from seed_exams.py
  'clat': 'clat',                   // "CLAT"
  'ailet': 'ailet',                 // "AILET"
  
  // Design - EXACT names from seed_exams.py  
  'nid dat': 'nid_dat',             // "NID DAT"
  'nata': 'nata',                   // "NATA"
  'uceed': 'uceed',                 // "UCEED"
  
  // CUET
  'cuet': 'cuet',                   // "CUET"
};

export const getExamInfo = (examId, examName = '') => {
  // examId is UUID, examName is actual name like "JEE Main", "NEET UG", etc.
  // We primarily match on examName (lowercase with spaces)
  
  const nameWithSpaces = examName?.toLowerCase().trim();
  
  // Check key map - keys are lowercase with spaces like 'jee main', 'neet ug'
  const mappedKey = EXAM_KEY_MAP[nameWithSpaces];
  
  if (mappedKey && EXAM_CAREER_INFO[mappedKey]) {
    return EXAM_CAREER_INFO[mappedKey];
  }

  // Try direct match on EXAM_CAREER_INFO keys (in case name is already in key format)
  if (nameWithSpaces && EXAM_CAREER_INFO[nameWithSpaces]) {
    return EXAM_CAREER_INFO[nameWithSpaces];
  }
  
  return null;
};

// Create a fallback exam object from career info
const createExamFromCareerInfo = (examId, careerInfo) => {
  if (!careerInfo) return null;
  return {
    id: examId,
    name: careerInfo.fullName?.split('(')[0]?.trim() || examId.replace(/_/g, ' ').toUpperCase(),
    full_name: careerInfo.fullName,
    conducting_body: careerInfo.conductedBy,
    description: careerInfo.overview,
    category: detectCategory(examId),
    config: {
      total_questions: careerInfo.totalQuestions || 100,
      duration: careerInfo.duration || 180,
      total_marks: careerInfo.totalMarks || 300
    }
  };
};

const detectCategory = (examId) => {
  const id = examId?.toLowerCase() || '';
  if (id.includes('jee') || id.includes('gate') || id.includes('bitsat')) return 'engineering';
  if (id.includes('neet') || id.includes('aiims')) return 'medical';
  if (id.includes('upsc') || id.includes('ssc') || id.includes('cds') || id.includes('nda')) return 'government';
  if (id.includes('cat') || id.includes('xat') || id.includes('mat')) return 'management';
  if (id.includes('ibps') || id.includes('sbi') || id.includes('rbi')) return 'banking';
  if (id.includes('clat') || id.includes('ailet')) return 'law';
  return 'other';
};

const ExamDetails = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [_apiError, setApiError] = useState(false);
  const [aiExamInfo, setAiExamInfo] = useState(null);
  const [adminExamInfo, setAdminExamInfo] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadExamDetails();
  }, [examId]);

  const loadExamDetails = async () => {
    try {
      const [examRes, subjectsRes, updatesRes] = await Promise.all([
        api.get(`/exams/${examId}`),
        api.get(`/exams/${examId}/subjects`),
        api.get(`/profile/updates`).catch(() => ({ data: [] }))
      ]);
      
      setExam(examRes.data);
      setSubjects(subjectsRes.data);
      
      // Filter updates for this exam
      const examUpdates = updatesRes.data.filter(u => u.exam_id === examId);
      setUpdates(examUpdates);
      setApiError(false);
      
      // Fetch admin-managed exam info (highest priority)
      try {
        const adminInfoRes = await examInfoAPI.getByExamId(examId);
        if (adminInfoRes.data?.info_data) {
          setAdminExamInfo(adminInfoRes.data.info_data);
        }
      } catch (adminErr) {
        // No admin-managed info, will fall through to AI/static
      }

      // Fetch AI-powered exam info as secondary source
      await fetchAIExamInfo(examRes.data?.name);
    } catch (error) {
      logger.error('Error loading exam details:', error);
      setApiError(true);
      
      // Try to use fallback data from local career info
      const fallbackInfo = getExamInfo(examId);
      if (fallbackInfo) {
        const fallbackExam = createExamFromCareerInfo(examId, fallbackInfo);
        setExam(fallbackExam);
      } else {
        toast.error('Failed to load exam details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAIExamInfo = async (examName) => {
    if (!examName) return;
    setLoadingAI(true);
    try {
      const aiInfoRes = await api.get(`/ai/exam-info/${encodeURIComponent(examName)}`);
      if (aiInfoRes.data) {
        setAiExamInfo(aiInfoRes.data);
        setLastUpdated(aiInfoRes.data.last_updated || new Date().toISOString());
      }
    } catch (aiError) {
      // AI exam info not available, using local data
    } finally {
      setLoadingAI(false);
    }
  };

  const refreshAIInfo = async () => {
    if (exam?.name) {
      toast.info('Refreshing exam info...');
      await fetchAIExamInfo(exam.name);
      toast.success('Exam info updated!');
    }
  };

  // Get comprehensive info - admin data > AI data > static config
  const careerInfo = adminExamInfo || aiExamInfo || getExamInfo(examId, exam?.name);
  // Try multiple ways to get subjects - examId first, then name
  const examSubjects = getExamSubjects(examId) || getExamSubjects(exam?.name || '') || [];

  // Merge all data sources for complete info
  const mergedInfo = {
    overview: adminExamInfo?.overview || aiExamInfo?.overview || careerInfo?.overview || exam?.description,
    fullName: adminExamInfo?.fullName || aiExamInfo?.fullName || careerInfo?.fullName || exam?.full_name,
    conductedBy: adminExamInfo?.conductedBy || aiExamInfo?.conductedBy || careerInfo?.conductedBy || exam?.conducting_body,
    totalCandidates: adminExamInfo?.totalCandidates || aiExamInfo?.totalCandidates || careerInfo?.totalCandidates,
    examFrequency: adminExamInfo?.examFrequency || aiExamInfo?.examFrequency || careerInfo?.examFrequency,
    jobOpportunities: adminExamInfo?.jobOpportunities || aiExamInfo?.jobOpportunities || careerInfo?.jobOpportunities || [],
    topColleges: adminExamInfo?.topColleges || aiExamInfo?.topColleges || careerInfo?.topColleges || [],
    whyPrepare: adminExamInfo?.whyPrepare || aiExamInfo?.whyPrepare || careerInfo?.whyPrepare || [],
    preparationTips: adminExamInfo?.preparationTips || aiExamInfo?.preparationTips || careerInfo?.preparationTips || [],
    importantDates: adminExamInfo?.importantDates2026 || aiExamInfo?.importantDates || aiExamInfo?.importantDates2026 || careerInfo?.importantDates2026 || {},
    eligibility: adminExamInfo?.eligibility || aiExamInfo?.eligibility || careerInfo?.eligibility || {},
    examPattern: adminExamInfo?.examPattern || aiExamInfo?.examPattern || careerInfo?.examPattern || {},
    collegePackages: adminExamInfo?.collegePackages || aiExamInfo?.collegePackages || careerInfo?.collegePackages || [],
    syllabus: adminExamInfo?.syllabus || aiExamInfo?.syllabus || careerInfo?.syllabus || [],
  };

  // Section visibility flags (admin can hide any section)
  const hidden = {
    about: adminExamInfo?.hideAbout === true,
    dates: adminExamInfo?.hideDates === true,
    whyPrepare: adminExamInfo?.hideWhyPrepare === true,
    career: adminExamInfo?.hideCareer === true,
    colleges: adminExamInfo?.hideColleges === true,
    pattern: adminExamInfo?.hidePattern === true,
    eligibility: adminExamInfo?.hideEligibility === true,
    tips: adminExamInfo?.hideTips === true,
    packages: adminExamInfo?.hidePackages === true,
    syllabus: adminExamInfo?.hideSyllabus === true,
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="lg" text="Loading exam details..." />
      </Layout>
    );
  }

  if (!exam) {
    return (
      <Layout>
        <div className="text-center py-20">
          <h2 className="text-2xl font-bold text-foreground">Exam not found</h2>
          <Button onClick={() => navigate('/exams')} className="mt-4">
            <ArrowLeft size={16} className="mr-2" />
            Back to Exams
          </Button>
        </div>
      </Layout>
    );
  }

  // Use exam data directly from database, with fallbacks to hardcoded info
  const config = {
    total_questions: exam.total_questions || mergedInfo.examPattern?.totalQuestions || 100,
    duration_minutes: exam.duration_minutes || 180,
    total_marks: exam.total_marks || (mergedInfo.examPattern?.sections?.reduce((sum, s) => sum + (s.marks || 0), 0)) || 300,
    marking_correct: exam.marking_correct || 4,
    marking_incorrect: exam.marking_incorrect || -1,
    negative_marking: exam.marking_incorrect !== 0
  };
  const examInfo = exam.exam_info || {};
  const pattern = examInfo.pattern || {};
  const eligibility = examInfo.eligibility || {};
  const syllabus = examInfo.syllabus || {};
  const dates = mergedInfo.importantDates;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 pb-12">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/exams')} className="mb-6 -ml-2">
          <ArrowLeft size={18} className="mr-2" />
          All Exams
        </Button>

        {/* Clean Header */}
        <div className="mb-8 pb-6 border-b border-border">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="text-6xl">{exam.icon}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">
                  {exam.category?.charAt(0).toUpperCase() + exam.category?.slice(1)}
                </Badge>
                {aiExamInfo && (
                  <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                    <Sparkles size={12} className="mr-1" /> AI Updated
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">{exam.name}</h1>
              {mergedInfo.fullName && (
                <p className="text-lg text-muted-foreground mb-1">{mergedInfo.fullName}</p>
              )}
              <p className="text-muted-foreground">
                {mergedInfo.conductedBy ? `Conducted by ${mergedInfo.conductedBy}` : ''}
              </p>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                  <FileText size={14} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{config.total_questions || '—'} Questions</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                  <Clock size={14} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{config.duration_minutes || '—'} Minutes</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                  <Users size={14} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{mergedInfo.totalCandidates || '—'} Candidates</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Button 
            onClick={() => navigate(`/test/create?exam=${examId}`)}
            className="h-12 text-base"
          >
            <FileText className="mr-2" size={18} />
            Take Full Test
          </Button>
          <Button 
            onClick={() => navigate(`/practice/${examId}`)}
            variant="outline"
            className="h-12 text-base"
          >
            <Target className="mr-2" size={18} />
            Practice Questions
          </Button>
        </div>

        {/* Last Updated Info */}
        {lastUpdated && (
          <div className="flex items-center justify-between mb-6 px-4 py-3 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw size={14} />
              <span>Last updated: {new Date(lastUpdated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={refreshAIInfo} disabled={loadingAI} className="text-xs">
              {loadingAI ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="ml-1">Refresh</span>
            </Button>
          </div>
        )}

        {/* SCROLLABLE CONTENT - All sections visible */}
        <div className="space-y-8">
          
          {/* Section 1: About this Exam */}
          {!hidden.about && (
          <Card className="p-6 rounded-2xl border border-border">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Info className="text-indigo-500" size={22} />
              About {exam.name}
            </h2>
            <p className="text-muted-foreground leading-relaxed text-lg">
              {mergedInfo.overview || `Prepare for ${exam.name} with comprehensive study materials, previous year questions, and expert guidance.`}
            </p>
            {mergedInfo.examFrequency && (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar size={14} />
                <span>{mergedInfo.examFrequency}</span>
              </div>
            )}
          </Card>
          )}

          {/* Section 2: Important Dates */}
          {!hidden.dates && Object.keys(dates).length > 0 && (
            <Card className="p-6 rounded-2xl border border-border">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Calendar className="text-blue-500" size={22} />
                Important Dates 2026
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(dates).map(([key, value]) => (
                  <div key={key} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-blue-600 dark:text-blue-400 mb-1 capitalize font-medium">
                      {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                    </p>
                    <p className="font-semibold text-foreground">{value}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Section 3: Why Prepare */}
          {!hidden.whyPrepare && mergedInfo.whyPrepare.length > 0 && (
            <Card className="p-6 rounded-2xl border border-border">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Lightbulb className="text-emerald-500" size={22} />
                Why Prepare for {exam.name}?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {mergedInfo.whyPrepare.map((reason, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <CheckCircle className="text-emerald-500 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-foreground">{reason}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Section 4: Career & Job Opportunities */}
          {!hidden.career && mergedInfo.jobOpportunities.length > 0 && (
            <Card className="p-6 rounded-2xl border border-border">
              <h2 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
                <Briefcase className="text-purple-500" size={22} />
                Career Opportunities after {exam.name}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Explore the career paths and salary packages available after clearing this exam
              </p>
              <div className="space-y-4">
                {mergedInfo.jobOpportunities.map((job, idx) => (
                  <div key={idx} className="p-5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-1">{job.title}</h3>
                        {job.description && (
                          <p className="text-sm text-muted-foreground mb-2">{job.description}</p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium">Top Recruiters:</span> {job.companies}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                        
                        <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{job.salary}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Section 5: Top Colleges/Institutes */}
          {!hidden.colleges && mergedInfo.topColleges.length > 0 && (
            <Card className="p-6 rounded-2xl border border-border">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <GraduationCap className="text-amber-500" size={22} />
                Top Institutes/Colleges
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {mergedInfo.topColleges.map((college, idx) => {
                  const name = typeof college === 'string' ? college : college?.name || '';
                  const detail = typeof college === 'object' ? (college?.cutoff || college?.avgPackage || '') : '';
                  return (
                  <div key={idx} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2">
                      <Star className="text-amber-500" size={16} />
                      <span className="text-sm font-medium text-foreground">{name}</span>
                    </div>
                    {detail && <p className="text-xs text-muted-foreground mt-1 ml-6">{detail}</p>}
                  </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Section 6: Exam Pattern */}
          {!hidden.pattern && (
          <Card className="p-6 rounded-2xl border border-border">
            <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
              <BarChart3 className="text-indigo-500" size={22} />
              Exam Pattern
            </h2>
            
            {/* Pattern Info - prioritize database values */}
            {(exam || mergedInfo.examPattern?.mode) && (
              <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {mergedInfo.examPattern?.mode && (
                    <div><span className="text-muted-foreground">Mode:</span> <span className="font-semibold text-foreground">{mergedInfo.examPattern.mode}</span></div>
                  )}
                  {mergedInfo.examPattern?.papers && (
                    <div><span className="text-muted-foreground">Papers:</span> <span className="font-semibold text-foreground">{mergedInfo.examPattern.papers}</span></div>
                  )}
                  <div><span className="text-muted-foreground">Marking:</span> <span className="font-semibold text-foreground">+{config.marking_correct} for correct, {config.marking_incorrect} for incorrect MCQ</span></div>
                  <div><span className="text-muted-foreground">Duration:</span> <span className="font-semibold text-foreground">{config.duration_minutes} minutes ({Math.floor(config.duration_minutes/60)} hours)</span></div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-5 bg-blue-50 dark:bg-blue-950/30 rounded-xl text-center">
                <FileText className="text-blue-500 mx-auto mb-2" size={24} />
                <p className="text-3xl font-bold text-foreground">{config.total_questions}</p>
                <p className="text-sm text-muted-foreground">Total Questions</p>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl text-center">
                <Clock className="text-emerald-500 mx-auto mb-2" size={24} />
                <p className="text-3xl font-bold text-foreground">{config.duration_minutes}</p>
                <p className="text-sm text-muted-foreground">Minutes</p>
              </div>
              <div className="p-5 bg-amber-50 dark:bg-amber-950/30 rounded-xl text-center">
                <Award className="text-amber-500 mx-auto mb-2" size={24} />
                <p className="text-3xl font-bold text-foreground">{config.total_marks}</p>
                <p className="text-sm text-muted-foreground">Total Marks</p>
              </div>
              <div className="p-5 bg-red-50 dark:bg-red-950/30 rounded-xl text-center">
                <TrendingUp className="text-red-500 mx-auto mb-2" size={24} />
                <p className="text-3xl font-bold text-foreground">{config.negative_marking ? 'Yes' : 'No'}</p>
                <p className="text-sm text-muted-foreground">Negative Marking</p>
              </div>
            </div>

            {/* Section-wise breakdown from database subjects */}
            {subjects && subjects.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-foreground mb-3">Section-wise Breakdown</h3>
                <div className="space-y-2">
                  {subjects.map((subject, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center justify-between">
                      <span className="font-medium text-foreground">{subject.name}</span>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{subject.questions_count || 25} Qs</span>
                        <span className="font-semibold text-foreground">{subject.marks || 100} marks</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {config.marking_scheme && (
              <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <h3 className="font-semibold text-foreground mb-3">Marking Scheme</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-emerald-500" size={18} />
                    <span className="text-foreground">Correct: <span className="font-bold text-emerald-600">+{config.marking_scheme.correct}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="text-red-500" size={18} />
                    <span className="text-foreground">Incorrect: <span className="font-bold text-red-600">{config.marking_scheme.incorrect}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="text-slate-400" size={18} />
                    <span className="text-foreground">Unattempted: <span className="font-bold">{config.marking_scheme.unattempted}</span></span>
                  </div>
                </div>
              </div>
            )}
          </Card>
          )}

          {/* Section 7: Exam Sections from API */}
          {config.sections && config.sections.length > 0 && (
            <Card className="p-6 rounded-2xl border border-border">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Shield className="text-violet-500" size={22} />
                Exam Sections
              </h2>
              <div className="space-y-3">
                {config.sections.map((section, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">{section.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {section.questions} questions • {section.duration_minutes} mins • {section.marks} marks
                      </p>
                    </div>
                    <Shield className="text-violet-500" size={24} />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Section 8: Syllabus & Chapters */}
          {!hidden.syllabus && (
          <Card className="p-6 rounded-2xl border border-border">
            <h2 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
              <BookOpen className="text-teal-500" size={22} />
              Syllabus & Chapters
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Based on Class 11th & 12th NCERT curriculum
            </p>
            {examSubjects.length > 0 ? (
              <div className="space-y-6">
                {examSubjects.map((subject) => {
                  // Try examId first, then exam name
                  const chapters = getSubjectChapters(examId, subject) || getSubjectChapters(exam.name, subject) || [];
                  return (
                    <div key={subject} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                      {/* Subject Header */}
                      <div className="p-4 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                          <Zap className="text-teal-500" size={18} />
                          {subject}
                        </h3>
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary">{chapters.length} Chapters</Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/practice/${examId}?subject=${subject}`)}
                            className="border-teal-200 text-teal-600 hover:bg-teal-50 dark:border-teal-800 dark:text-teal-400"
                          >
                            Practice
                            <ChevronRight size={16} className="ml-1" />
                          </Button>
                        </div>
                      </div>
                      {/* All Chapters List */}
                      <div className="p-4 bg-white dark:bg-slate-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {chapters.map((chapter, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2.5 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm border border-slate-100 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-700 transition-colors cursor-pointer">
                              <span className="w-5 h-5 flex items-center justify-center bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 rounded text-xs font-medium flex-shrink-0">
                                {idx + 1}
                              </span>
                              <span className="text-foreground truncate">{chapter}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : Object.keys(syllabus).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(syllabus).map(([subject, topics]) => (
                  <div key={subject} className="p-5 bg-slate-50 dark:bg-slate-900 rounded-xl">
                    <h3 className="text-lg font-bold text-foreground mb-3">{subject}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Array.isArray(topics) && topics.map((topic, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg text-sm">
                          <CheckCircle className="text-emerald-500 flex-shrink-0" size={14} />
                          <span className="text-foreground">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="text-muted-foreground mx-auto mb-4" size={48} />
                <h3 className="text-lg font-semibold text-foreground mb-2">Syllabus Coming Soon</h3>
                <p className="text-muted-foreground">We're working on adding the complete syllabus for this exam.</p>
              </div>
            )}
          </Card>
          )}

          {/* Section 9: Preparation Tips */}
          {!hidden.tips && mergedInfo.preparationTips.length > 0 && (
            <Card className="p-6 rounded-2xl border border-border">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Rocket className="text-violet-500" size={22} />
                Preparation Strategy
              </h2>
              <div className="space-y-3">
                {mergedInfo.preparationTips.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white dark:bg-slate-900 rounded-xl">
                    <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      {idx + 1}
                    </div>
                    <span className="text-foreground pt-1">{tip}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Section 10: Eligibility Criteria */}
          {!hidden.eligibility && (
          <Card className="p-6 rounded-2xl border border-border">
            <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
              <CheckCircle className="text-emerald-500" size={22} />
              Eligibility Criteria
            </h2>
            
            {Object.keys(mergedInfo.eligibility).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mergedInfo.eligibility.age && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <Users className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Age Limit</p>
                    <p className="font-semibold text-foreground">{mergedInfo.eligibility.age}</p>
                  </div>
                )}
                {mergedInfo.eligibility.qualification && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <GraduationCap className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Qualification</p>
                    <p className="font-semibold text-foreground text-sm">{mergedInfo.eligibility.qualification}</p>
                  </div>
                )}
                {mergedInfo.eligibility.minimumMarks && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <TrendingUp className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Minimum Marks Required</p>
                    <p className="font-semibold text-foreground text-sm">{mergedInfo.eligibility.minimumMarks}</p>
                  </div>
                )}
                {mergedInfo.eligibility.attempts && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <RefreshCw className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Number of Attempts</p>
                    <p className="font-semibold text-foreground">{mergedInfo.eligibility.attempts}</p>
                  </div>
                )}
                {mergedInfo.eligibility.yearOfPassing && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <Calendar className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Year of Passing</p>
                    <p className="font-semibold text-foreground">{mergedInfo.eligibility.yearOfPassing}</p>
                  </div>
                )}
              </div>
            ) : Object.keys(eligibility).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {eligibility.age_limit && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <Users className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Age Limit</p>
                    <p className="font-semibold text-foreground">{eligibility.age_limit}</p>
                  </div>
                )}
                {eligibility.qualification && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <GraduationCap className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Qualification</p>
                    <p className="font-semibold text-foreground text-sm">{eligibility.qualification}</p>
                  </div>
                )}
                {eligibility.minimum_percentage && (
                  <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <TrendingUp className="text-indigo-500 mb-2" size={24} />
                    <p className="text-sm text-muted-foreground mb-1">Minimum Marks</p>
                    <p className="font-semibold text-foreground text-sm">{eligibility.minimum_percentage}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Eligibility criteria will be updated soon.</p>
              </div>
            )}
          </Card>
          )}

          {/* Section 11: College Packages & Salary Info */}
          {!hidden.packages && mergedInfo.collegePackages?.length > 0 && (
            <Card className="p-6 rounded-2xl border border-border bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <DollarSign className="text-emerald-500" size={22} />
                Salary & Placement Packages by College
              </h2>
              <div className="space-y-3">
                {mergedInfo.collegePackages.map((pkg, idx) => (
                  <div key={idx} className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-emerald-100 dark:border-emerald-900 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="text-emerald-500" size={20} />
                      <span className="font-semibold text-foreground">{pkg.college}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Avg Package: </span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">{pkg.avgPackage}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Highest: </span>
                        <span className="font-bold text-amber-600 dark:text-amber-400">{pkg.highestPackage}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Section 12: Official Website */}
          {exam.official_website && (
            <Card className="p-6 rounded-2xl border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="text-blue-500" size={24} />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Official Information</h3>
                    <p className="text-sm text-muted-foreground">Get the latest updates from the official website</p>
                  </div>
                </div>
                <Button onClick={() => window.open(exam.official_website, '_blank')}>
                  Visit Website <ExternalLink size={16} className="ml-2" />
                </Button>
              </div>
            </Card>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default ExamDetails;
